import React, { useEffect, useRef, useState } from "react";
import apiClient from "../../services/apiClient";
import { useCompany } from "../../context/CompanyContext";

/**
 * InviteCompanyModal
 *
 * ✔ Company-to-company invite
 * ✔ Uses correct API contract
 * ✔ Strong validation + UX
 */

export default function InviteCompanyModal({
  open,
  onClose,
  onSuccess,
}) {
  const { activeCompany } = useCompany();

  const [companyId, setCompanyId] = useState("");
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const inputRef = useRef(null);

  /* ===================================================== */
  /* INIT                                                  */
  /* ===================================================== */

  useEffect(() => {
    if (!open) return;

    setCompanyId("");
    setMessage("");
    setError("");

    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (!open) return null;

  /* ===================================================== */
  /* VALIDATION                                            */
  /* ===================================================== */

  const validate = () => {
    const id = companyId.trim();

    if (!id) return "Company ID is required";

    if (id === activeCompany?._id) {
      return "You cannot invite your own company";
    }

    // strict ObjectId check
    if (!/^[0-9a-fA-F]{24}$/.test(id)) {
      return "Invalid Company ID format";
    }

    return null;
  };

  /* ===================================================== */
  /* SUBMIT                                                */
  /* ===================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");

      await apiClient.post("/contracts/invite", {
        contractorCompanyId: companyId.trim(), // ✅ FIXED
        message: message.trim() || undefined,
      });

      onClose?.();
      onSuccess?.();

    } catch (err) {
      console.error("Invite error:", err);

      setError(
        err?.message ||
        err?.data?.message ||
        "Failed to send invite"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Invite Contractor
          </h2>
          <p className="text-sm text-gray-500">
            Enter the company ID to connect
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">

            {/* Company ID */}
            <div>
              <label className="block text-sm text-gray-600">
                Company ID
              </label>
              <input
                ref={inputRef}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="e.g. 69bdb8d0aafc9354257429bc"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm text-gray-600">
                Message (optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Add a note..."
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                {error}
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-5 py-4 border-t bg-white">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send Invite"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}